import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core';
import StudentAnswerChip from './StudentAnswerChip';
import { AttemptAnswer, QuestionSetAttempt } from '../../../../../../config/api/models/Answers';
import {
  getMessageByQuestionType,
  QuestionType,
} from '../../../../../../config/api/models/QuestionType';
import {
  isSolutionWithSubContent,
  SolutionAnswer,
  SolutionSubContent,
} from '../../../../../../config/api/models/Solutions';
import ReactHtmlParser from 'react-html-parser';
import { preProcessLatex } from '../../../../helpers';
import { StyledAccordion, StyledAccordionSummary } from '../Atoms';
import { AccordionIcon, StyledAccordionDetails, StyledDivider, StyledQuestionTitle } from './Atoms';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import ScoreSummaryContent from './ScoreSummaryContent';
import AlgebraKitExercise from 'src/modules/shared/components/AlgebraKit/AlgebraKitExercise';
import { AttemptLabel } from '../solution/Atoms';

type Props = {
  questionType?: QuestionType;
  attempt: QuestionSetAttempt;
  label: string;
  index: number;
  solutionAnswers?: SolutionSubContent[];
};

const StudentQuestionSetAttempt: FC<Props> = ({
  questionType,
  attempt,
  label,
  index,
  solutionAnswers,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (index === 0) setExpanded(true);
  }, [index, setExpanded]);

  return (
    <StyledAccordion onChange={() => setExpanded((prev) => !prev)} defaultExpanded={index === 0}>
      <StyledAccordionSummary>
        <AttemptLabel>
          <AccordionIcon
            icon={expanded ? SpriteIconsLight.ANGLE_UP : SpriteIconsLight.ANGLE_DOWN}
            iconSize="md"
            color={theme.palette.text.primary}
          />
          {t(`trajectory.sections.shared.answers.${label}`)}
        </AttemptLabel>
        <ScoreSummaryContent
          score={attempt.score}
          maxScore={attempt.maxScore}
          scoredAtDate={attempt.scoredAt.date}
        />
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {attempt.scored_session_id && <AlgebraKitExercise sessionId={attempt.scored_session_id} />}
        {!attempt.scored_session_id &&
          Object.entries(attempt.questions).map(([key, value], index) => {
            let questionFromSolutions: string | undefined;

            if (
              solutionAnswers?.[index] != null &&
              solutionAnswers[index].subContent != null &&
              solutionAnswers[index].subContent.length > 0
            ) {
              if (isSolutionWithSubContent(solutionAnswers[index].subContent[0])) {
                questionFromSolutions = undefined;
              } else {
                questionFromSolutions = (solutionAnswers[index].subContent[0] as SolutionAnswer)
                  .question;
              }
            }

            questionFromSolutions = preProcessLatex(questionFromSolutions);

            return (
              <React.Fragment key={key}>
                {index !== 0 && <StyledDivider />}
                <StyledQuestionTitle>
                  {ReactHtmlParser(questionFromSolutions || value.title)}
                </StyledQuestionTitle>
                <div>
                  {value.answers && value.answers.length > 0 ? (
                    <>
                      {value.answers.map((answer: AttemptAnswer, index: number) => (
                        <StudentAnswerChip
                          key={index}
                          answer={answer.answer}
                          correct={answer.correct}
                          grey={questionType === QuestionType.ESSAY}
                        />
                      ))}
                    </>
                  ) : (
                    <>{getMessageByQuestionType(t, questionType)}</>
                  )}
                </div>
              </React.Fragment>
            );
          })}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default StudentQuestionSetAttempt;
